<h1 mat-dialog-title>Update stills file specification</h1>
<mat-dialog-content class="dialog-body">
  <section class="field-group">
    <mat-form-field appearance="outline" class="form-field">
      <mat-label>Name</mat-label>
      <input matInput [formControl]="nameControl" type="text" />
    </mat-form-field>
  </section>

  <section class="field-group">
    <h3 class="group-title">Format</h3>
    <mat-form-field appearance="outline" class="form-field">
      <mat-label>Format</mat-label>
      <mat-select [formControl]="formatControl">
        @for (k of formatKinds; track k) {
          <mat-option [value]="k">{{ formatLabel(k) }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    @if (isJpg) {
      <mat-form-field appearance="outline" class="form-field">
        <mat-label>Quality (1–100)</mat-label>
        <input matInput [formControl]="jpgQualityControl" type="number" min="1" max="100" />
      </mat-form-field>
    }

    @if (isPng) {
      <mat-form-field appearance="outline" class="form-field">
        <mat-label>Bit depth</mat-label>
        <mat-select [formControl]="pngBitDepthControl">
          @for (b of bitDepths; track b) {
            <mat-option [value]="b">{{ b }}-bit</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }

    @if (isTiff) {
      <mat-form-field appearance="outline" class="form-field">
        <mat-label>Bit depth</mat-label>
        <mat-select [formControl]="tiffBitDepthControl">
          @for (b of bitDepths; track b) {
            <mat-option [value]="b">{{ b }}-bit</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }

    @if (isWebp) {
      <div class="toggle-row">
        <span class="toggle-label">Compression</span>
        <mat-button-toggle-group [formControl]="webpCompressionModeControl">
          @for (m of webpCompressionModes; track m) {
            <mat-button-toggle [value]="m">{{ webpCompressionModeLabel(m) }}</mat-button-toggle>
          }
        </mat-button-toggle-group>
      </div>

      @if (isWebpLossy) {
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Quality (1–100)</mat-label>
          <input matInput [formControl]="webpQualityControl" type="number" min="1" max="100" />
        </mat-form-field>
      }
    }

    <mat-form-field appearance="outline" class="form-field">
      <mat-label>Colour space</mat-label>
      <mat-select [formControl]="colorSpaceControl">
        @for (c of colorSpaces; track c) {
          <mat-option [value]="c">{{ c }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="form-field">
      <mat-label>DPI</mat-label>
      <input matInput [formControl]="dpiControl" type="number" min="1" />
    </mat-form-field>
  </section>

  <section class="field-group">
    <h3 class="group-title">Dimensions</h3>
    <mat-form-field appearance="outline" class="form-field">
      <mat-label>Mode</mat-label>
      <mat-select [formControl]="dimensionsModeControl">
        @for (m of dimensionsModes; track m) {
          <mat-option [value]="m">{{ dimensionsModeLabel(m) }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    @if (showsWidthField || showsHeightField) {
      <div class="pair-row">
        @if (showsWidthField) {
          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Width (px)</mat-label>
            <input matInput [formControl]="widthControl" type="number" min="1" step="1" />
            @if (widthControl.hasError('min') || widthControl.hasError('required')) {
              <mat-error>Width must be at least 1</mat-error>
            }
            @if (widthControl.hasError('integer')) {
              <mat-error>Width must be a whole number</mat-error>
            }
          </mat-form-field>
        }
        @if (showsHeightField) {
          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Height (px)</mat-label>
            <input matInput [formControl]="heightControl" type="number" min="1" step="1" />
            @if (heightControl.hasError('min') || heightControl.hasError('required')) {
              <mat-error>Height must be at least 1</mat-error>
            }
            @if (heightControl.hasError('integer')) {
              <mat-error>Height must be a whole number</mat-error>
            }
          </mat-form-field>
        }
      </div>
    }

    @if (showsLongEdgeField) {
      <div class="pair-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Long edge (px)</mat-label>
          <input matInput [formControl]="longEdgeControl" type="number" min="1" step="1" />
          @if (longEdgeControl.hasError('min') || longEdgeControl.hasError('required')) {
            <mat-error>Long edge must be at least 1</mat-error>
          }
          @if (longEdgeControl.hasError('integer')) {
            <mat-error>Long edge must be a whole number</mat-error>
          }
        </mat-form-field>
        @if (showsShortEdgeField) {
          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Short edge (px)</mat-label>
            <input matInput [formControl]="shortEdgeControl" type="number" min="1" step="1" />
            @if (shortEdgeControl.hasError('min') || shortEdgeControl.hasError('required')) {
              <mat-error>Short edge must be at least 1</mat-error>
            }
            @if (shortEdgeControl.hasError('integer')) {
              <mat-error>Short edge must be a whole number</mat-error>
            }
          </mat-form-field>
        }
      </div>
    }
  </section>

  <section class="field-group">
    <h3 class="group-title">Subject cropping</h3>
    <div class="toggle-row">
      <span class="toggle-label">Mode</span>
      <mat-button-toggle-group [formControl]="subjectCroppingModeControl">
        @for (m of subjectCroppingModes; track m) {
          <mat-button-toggle [value]="m">{{ subjectCroppingModeLabel(m) }}</mat-button-toggle>
        }
      </mat-button-toggle-group>
    </div>

    @if (isSubjectCroppingOn) {
      <mat-form-field appearance="outline" class="form-field">
        <mat-label>Whitespace percent (1–49)</mat-label>
        <input
          matInput
          [formControl]="whitespacePercentControl"
          type="number"
          min="1"
          max="49"
          step="1"
        />
        @if (whitespacePercentControl.hasError('min') || whitespacePercentControl.hasError('required')) {
          <mat-error>Whitespace must be at least 1</mat-error>
        }
        @if (whitespacePercentControl.hasError('max')) {
          <mat-error>Whitespace must be at most 49</mat-error>
        }
        @if (whitespacePercentControl.hasError('integer')) {
          <mat-error>Whitespace must be a whole number</mat-error>
        }
      </mat-form-field>
    }
  </section>

  @if (showsBackgroundSection) {
    <section class="field-group">
      <h3 class="group-title">Background</h3>
      <div class="colour-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>Background colour (hex)</mat-label>
          <input
            matInput
            [value]="colorRgb(backgroundColourControl.value)"
            (input)="setColor(colourText.value)"
            #colourText
            type="text"
            placeholder="#ffffff"
          />
          <div
            matSuffix
            class="colour-swatch"
            [class.colour-swatch--checkered]="isTransparent(backgroundColourControl.value)"
            [style.background-color]="
              isTransparent(backgroundColourControl.value)
                ? null
                : colorRgb(backgroundColourControl.value)
            "
            (click)="colorPicker.click()"
          >
            <span class="colour-swatch__hex">{{ colorRgb(backgroundColourControl.value) }}</span>
            <input
              #colorPicker
              type="color"
              class="colour-swatch__native"
              [value]="colorRgb(backgroundColourControl.value)"
              (input)="setColor(colorPicker.value)"
            />
          </div>
        </mat-form-field>
      </div>
      <mat-checkbox
        color="primary"
        [checked]="isTransparent(backgroundColourControl.value)"
        (change)="setTransparent($event.checked)"
      >
        Transparent background
      </mat-checkbox>
    </section>
  }

  <section class="field-group">
    <h3 class="group-title">Reason</h3>
    <mat-form-field appearance="outline" class="form-field">
      <mat-label>Reason</mat-label>
      <mat-select [formControl]="reasonControl">
        @for (r of selectableReasons; track r) {
          <mat-option [value]="r">{{ reasonLabel(r) }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    @if (isOther) {
      <mat-form-field appearance="outline" class="form-field">
        <mat-label>Detail</mat-label>
        <input matInput [formControl]="detailControl" type="text" />
      </mat-form-field>
    }
  </section>
</mat-dialog-content>
<mat-dialog-actions align="end" class="actions">
  <button mat-stroked-button type="button" (click)="cancel()">Cancel</button>
  <button
    mat-stroked-button
    type="button"
    color="buttonSuccess"
    [disabled]="!canSubmit"
    (click)="submit()"
  >
    Save
  </button>
</mat-dialog-actions>
